<script lang="ts">
const uploadErrorsHeaders = [
  { text: 'Row', value: 'row' },
  { text: 'Error', value: 'errMsg' },
];
</script>

<script setup lang="ts">
import { DialogFormStatus } from '@/modules/common/types/dialog';
import { ErrorItem, Security } from '@/modules/common/models';
import { ApiError } from '@/utils/errors';
import { onMounted, shallowRef } from 'vue';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import MultipleSecuritySelector from '@/modules/common/components/MultipleSecuritySelector.vue';
import Uploader from '@/modules/common/components/Uploader.vue';
import UploadParseErrors from '@/modules/common/components/UploadParseErrors.vue';
import type { UploadMethod } from '@/modules/common/types/upload';
import {
  WatchlistItemCreateRequest,
  WatchlistItemUpdateRequest,
} from '@/modules/marketplace/models';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { minLength, required, useRules } from '@/composables/vuetify-validation';

const emit = defineEmits<{
  (event: 'refresh-watchlists' | 'close-modal'): void;
}>();

const props = withDefaults(
  defineProps<{ displayId?: string | null; initialStep?: 'form' | 'upload' }>(),
  { displayId: null, initialStep: 'form' }
);

const snackbar = useSnackbarManager();

const marketplaceApiService = useMarketplaceApiService();

const securities = shallowRef<Security[]>([]);
const name = shallowRef<string | null>(null);

const formStatus = shallowRef<DialogFormStatus>('idle');
const apiError = shallowRef<string | null>(null);
const step = shallowRef<'form' | 'upload' | 'parse-errors'>('form');
const uploadMethod = shallowRef<UploadMethod | null>(null);
const errors = shallowRef<ErrorItem[]>([]);

const { rules, isValidating, validateAndSubmit } = useRules<{ name: string }>({
  name: {
    rules: [(v: string) => required(v), (v: string) => minLength(v, 2)],
  },
});

async function submitForm(): Promise<void> {
  if (formStatus.value !== 'idle') {
    return;
  }

  formStatus.value = 'submitting';

  try {
    const payload = {
      name: name.value as string,
      securities: securities.value,
      shared: false,
    };

    if (props.displayId !== null) {
      await marketplaceApiService.updateWatchlist({
        displayId: props.displayId,
        payload: WatchlistItemUpdateRequest.fromModel(payload),
      });
    } else {
      await marketplaceApiService.createWatchlist(WatchlistItemCreateRequest.fromModel(payload));
    }

    snackbar.confirm(`Watchlist successfully ${props.displayId ? 'updated' : 'created'}.`);
    emit('refresh-watchlists');
    closeDialog();
  } catch (err) {
    apiError.value = i18nServerMessage(err as ApiError);
  } finally {
    formStatus.value = 'idle';
  }
}

function goToForm(securitiesArray: Security[]): void {
  securities.value = securitiesArray;
  step.value = 'form';
}

function goToParseErrors(errorsItems: ErrorItem[]): void {
  errors.value = errorsItems;
  step.value = 'parse-errors';
}

function closeDialog(): void {
  emit('close-modal');
}

onMounted(async () => {
  if (props.displayId === null) {
    step.value = props.initialStep;
    return;
  }

  // edit mode: fetch details and populate form
  try {
    const res = await marketplaceApiService.fetchWatchlistWithSecurities(props.displayId);
    name.value = res.name;
    securities.value = res.securities;
  } catch (err) {
    apiError.value = (err as ApiError).message;
  }
});

// Expose for testing
defineExpose({
  test: {
    setName: (val: string) => {
      name.value = val;
    },
    setSecurities: (val: Security[]) => {
      securities.value = val;
    },
    submit: submitForm,
  },
});
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    data-test-watchlist-form="dialog"
    max-width="600"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <Uploader
      v-if="step === 'upload'"
      :execute="(file) => marketplaceApiService.uploadWatchlistSecurities(file)"
      :upload-method="uploadMethod"
      :with-dummy-header="false"
      @close-modal="step = 'form'"
      @parse-errors="goToParseErrors($event)"
      @upload="goToForm($event.securities)"
      @upload-method="uploadMethod = $event"
    >
      <template #title>Upload Watchlist Securities</template>
      <template #description>
        <div>Expected column:</div>
        <div>Ticker or CUSIP</div>
      </template>
    </Uploader>

    <UploadParseErrors
      v-if="step === 'parse-errors'"
      :errors="errors"
      :headers="uploadErrorsHeaders"
      :upload-method="uploadMethod"
      @back="step = 'upload'"
      @parse-errors="goToForm($event)"
    >
      <template #title>Failed</template>
      <template #item="{ item }">
        <tr>
          <td>{{ item.row }}</td>
          <td>{{ item.errormsg }}</td>
        </tr>
      </template>
    </UploadParseErrors>

    <VForm v-if="step === 'form'" novalidate @submit.prevent>
      <VCard class="d-flex flex-column">
        <VCardTitle>
          <span class="headline">{{ displayId ? 'Edit' : 'Create' }} Watchlist</span>
          <VBtn class="close-icon" icon @click="closeDialog()">
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </VCardTitle>

        <VCardText>
          <VContainer>
            <VTextField
              v-model="name"
              v-bind="rules.name"
              :autofocus="displayId === null"
              class="mt-n4"
              label="Name"
            />

            <MultipleSecuritySelector :securities.sync="securities" :select-eligible-only="false" />
          </VContainer>

          <VRow v-if="apiError">
            <VCol class="pa-0 px-1 col-6 offset-3 mt-4">
              <div class="error v-alert v-alert--dense text--primary text-body-2 text-center">
                {{ apiError }}
              </div>
            </VCol>
          </VRow>

          <VContainer v-else class="mt-4 text-right">
            <div v-if="securities.length">
              {{ securities.length }}
              {{ securities.length === 1 ? 'security' : 'securities' }} in list
            </div>
            <div v-else>No securities have been added yet.</div>
          </VContainer>
        </VCardText>

        <VCardActions v-shortkey="['enter']" class="d-flex py-4 mt-n6" @shortkey="submitForm()">
          <div class="d-flex flex-grow-1 justify-space-between align-end">
            <VBtn color="secondary" @click="closeDialog()"> Cancel </VBtn>
            <VBtn
              color="primary"
              data-test-watchlist-form="submit"
              :disabled="isValidating"
              :loading="isValidating"
              @click="validateAndSubmit(submitForm)"
            >
              {{ displayId ? 'Save' : 'Create' }}
            </VBtn>
          </div>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
.close-icon {
  top: 0.4rem;
  right: 0.4rem;
  position: absolute;
}
</style>
